import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { ExportMenu, ListPageShell, PageHeader, SavedViewsBar, Table } from '~/components/data';
import { NewRunModal } from '~/components/domain';
import { Button, DateRangePicker, Select } from '~/components/primitives';
import { RuntimeStatusPill, StatusBadge } from '~/components/status';
import { type RunFilter, fetchRuns } from '~/lib/api/runs';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { RUNTIME_STATUS_LABELS, TASK_TYPE_LABELS, labelOf } from '~/lib/i18n/labels';
import { useShortcut } from '~/lib/shortcuts/useShortcuts';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/runs/')({
  staticData: { minMainWidth: 960 },
  component: RunsList,
});

function RunsList(): JSX.Element {
  const [filter, setFilter] = useState<RunFilter>({});
  const [newRunOpen, setNewRunOpen] = useState(false);
  const [activeViewId, setActiveViewId] = useState<string | undefined>();
  useShortcut('n', () => setNewRunOpen(true), m.route_dashboard_shortcut_new_run());

  const q = useQuery({
    queryKey: ['runs', filter],
    queryFn: () => fetchRuns(filter),
  });
  const state = useListPageState(q);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_runs()}
        actions={
          <>
            <ExportMenu resource="runs" filters={filter as Record<string, string | undefined>} />
            <Button leadingIcon={<Plus size={14} />} onClick={() => setNewRunOpen(true)}>
              {m.route_dashboard_btn_new_run()}
            </Button>
          </>
        }
      />

      <SavedViewsBar
        resource="runs"
        currentFilters={filter}
        onApply={(v) => {
          setFilter(v.filters as RunFilter);
          setActiveViewId(v.id);
        }}
        activeViewId={activeViewId}
      />

      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.route_conflicts_filters_heading()}</h3>
        <div className={shared.formGrid2}>
          <Select
            label={m.route_runs_index_filter_status()}
            value={filter.status ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_runs_index_filter_all() },
              { value: 'completed', label: RUNTIME_STATUS_LABELS.completed },
              { value: 'running', label: RUNTIME_STATUS_LABELS.running },
              { value: 'waiting_approval', label: RUNTIME_STATUS_LABELS.waiting_approval },
              { value: 'failed', label: RUNTIME_STATUS_LABELS.failed },
            ]}
          />
          <Select
            label={m.route_runs_index_filter_task_type()}
            value={filter.task_type ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, task_type: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_runs_index_filter_all() },
              { value: 'inventory_reconcile', label: TASK_TYPE_LABELS.inventory_reconcile },
              { value: 'writeoff_review', label: TASK_TYPE_LABELS.writeoff_review },
              { value: 'cost_draft', label: TASK_TYPE_LABELS.cost_draft },
              { value: 'anomaly_investigation', label: TASK_TYPE_LABELS.anomaly_investigation },
              { value: 'period_close_draft', label: TASK_TYPE_LABELS.period_close_draft },
            ]}
          />
        </div>
        {/* FE-19: date range filter (period_from/period_to). */}
        {/* FE-19 honest-audit: initiated_by select added. */}
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <DateRangePicker
            label="Период запуска"
            value={{ from: filter.date_from ?? null, to: filter.date_to ?? null }}
            onChange={(r) =>
              setFilter((f) => ({
                ...f,
                date_from: r.from ?? undefined,
                date_to: r.to ?? undefined,
              }))
            }
          />
          <Select
            label="Инициатор"
            value={filter.initiated_by ?? ''}
            onChange={(e) =>
              setFilter((f) => ({ ...f, initiated_by: e.target.value || undefined }))
            }
            options={[
              { value: '', label: m.route_runs_index_filter_all() },
              { value: 'operator', label: 'Оператор' },
              { value: 'agent', label: 'Агент' },
              { value: 'scheduler', label: 'Планировщик' },
            ]}
          />
        </div>
      </section>

      <ListPageShell
        state={state}
        emptyTitle={m.route_runs_index_empty_title()}
        emptyDescription={m.route_runs_index_empty_description()}
        emptyAction={
          <Button
            variant="primary"
            leadingIcon={<Plus size={14} />}
            onClick={() => setNewRunOpen(true)}
          >
            {m.route_runs_index_btn_new()}
          </Button>
        }
      >
        {(rows) => (
          <Table
            ariaLabel={m.route_runs_index_aria()}
            data={rows}
            rowKey={(r) => r.id}
            stickyHeader
            columns={[
              {
                // FE-18: ID column shows compact task-type label + uuid8.
                id: 'id',
                header: m.common_column_id(),
                accessor: (r) => (
                  <Link to="/runs/$runId" params={{ runId: r.id }} className={shared.link}>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <strong style={{ fontSize: 13 }}>
                        {labelOf(TASK_TYPE_LABELS, r.task_type as keyof typeof TASK_TYPE_LABELS)}
                      </strong>
                      <span className={shared.mono} style={{ fontSize: 11, opacity: 0.7 }}>
                        {r.id.slice(0, 8)}
                      </span>
                    </span>
                  </Link>
                ),
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (r: (typeof rows)[number]) => entities[r.entity_id]?.name ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                id: 'period',
                header: m.route_runs_index_col_period(),
                accessor: (r) => `${r.period.date_from} → ${r.period.date_to}`,
                mono: true,
              },
              {
                id: 'runtime',
                header: m.route_runs_index_col_runtime(),
                accessor: (r) => <RuntimeStatusPill runtime_status={r.runtime_status} size="sm" />,
              },
              {
                id: 'business',
                header: m.route_runs_index_col_business(),
                accessor: (r) =>
                  r.business_status ? <StatusBadge status={r.business_status} size="sm" /> : '—',
              },
              {
                id: 'started_at',
                header: m.route_runs_index_col_started(),
                accessor: (r) => (r.started_at ?? r.created_at).slice(0, 16).replace('T', ' '),
                mono: true,
              },
              {
                // FE-20: duration column (mm:ss when started/finished available).
                id: 'duration',
                header: 'Длительность',
                width: '110px',
                mono: true,
                align: 'right' as const,
                accessor: (r) => {
                  if (!r.started_at || !r.finished_at) return '—';
                  const ms = new Date(r.finished_at).getTime() - new Date(r.started_at).getTime();
                  if (ms < 0) return '—';
                  const totalSeconds = Math.floor(ms / 1000);
                  const m = Math.floor(totalSeconds / 60);
                  const s = totalSeconds % 60;
                  if (m < 60) return `${m}:${String(s).padStart(2, '0')}`;
                  const h = Math.floor(m / 60);
                  return `${h}ч ${m % 60}м`;
                },
              },
              {
                // FE-20 honest-audit: tool_calls count column (denorm from RunSummary if BE provides).
                id: 'tool_calls',
                header: 'Вызовов',
                width: '90px',
                mono: true,
                align: 'right' as const,
                accessor: (r) => {
                  const calls = (r as { tool_calls_count?: number | null }).tool_calls_count;
                  return calls != null ? String(calls) : '—';
                },
              },
              {
                // FE-20 honest-audit: SLA pill для waiting_approval / waiting_external.
                // Yellow >4h, red >24h since started_at.
                id: 'sla',
                header: 'SLA',
                width: '80px',
                accessor: (r) => {
                  const waiting =
                    r.runtime_status === 'waiting_approval' ||
                    r.runtime_status === 'waiting_external';
                  if (!waiting || !r.started_at) return '—';
                  const hours = Math.floor(
                    (Date.now() - new Date(r.started_at).getTime()) / 3_600_000,
                  );
                  if (hours <= 4) return null;
                  const color = hours > 24 ? 'var(--color-danger)' : 'var(--color-warn)';
                  const label = hours < 24 ? `${hours}ч` : `${Math.floor(hours / 24)}д`;
                  return (
                    <span
                      title={hours > 24 ? `Просрочено >24ч: ${label}` : `Внимание >4ч: ${label}`}
                      style={{
                        padding: '2px 6px',
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        background: hours > 24 ? 'var(--danger-bg-15)' : 'var(--warn-bg-15)',
                        color,
                        borderRadius: 3,
                      }}
                    >
                      {label}
                    </span>
                  );
                },
              },
            ]}
          />
        )}
      </ListPageShell>

      <NewRunModal open={newRunOpen} onClose={() => setNewRunOpen(false)} />
    </div>
  );
}
